import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, inject, computed } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatMenuModule } from "@angular/material/menu";
import { NavigationEnd, Router, RouterLink } from "@angular/router";
import { IUserDetails } from "@modals/app.modal";
import { AuthService } from "@services/auth.service";
import { HttpService } from "@services/http.service";
import { Constants } from "@config/constants";
import { ToastService } from "@services/toast.service";
import { Messages } from "@config/messages";
import { AppService } from "@services/app.service";
import { LanguageService, SupportedLanguage } from "@services/language.service";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription, filter, startWith } from "rxjs";
import { CreateFlowStateService } from "@services/create-flow-state.service";

@Component({
  selector: "kzn-header-create",
  standalone: true,
  imports: [CommonModule, MatToolbarModule, MatButtonModule, MatIconModule, MatMenuModule, TranslateModule, RouterLink],
  templateUrl: "./header-create.component.html",
  styleUrl: "./header-create.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class HeaderCreateComponent implements OnInit, OnDestroy {
  @Input() showSidebarToggle = true;

  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() toggleKapybara = new EventEmitter<void>();

  userDetails: IUserDetails | undefined;
  currentLang: SupportedLanguage = "en";

  /** Centered stepper config; rendered only when current route equals `/create/new`. */
  readonly wizardDotsStandard: ReadonlyArray<{ step: number; labelKey: string; icon: string }> = [
    { step: 0, labelKey: "wizardStepDocument", icon: "description" },
    { step: 1, labelKey: "wizardStepRequestDetails", icon: "group" },
    { step: 2, labelKey: "wizardStepTags", icon: "text_fields" },
    { step: 3, labelKey: "wizardStepSend", icon: "send" },
  ];

  readonly wizardDotsSelfSign: ReadonlyArray<{ step: number; labelKey: string; icon: string }> = [
    { step: 0, labelKey: "wizardStepDocument", icon: "description" },
    { step: 2, labelKey: "wizardStepTags", icon: "text_fields" },
    { step: 3, labelKey: "wizardStepSend", icon: "send" },
  ];

  readonly wizardDotsBulk: ReadonlyArray<{ step: number; labelKey: string; icon: string }> = [
    { step: 0, labelKey: "bulkStepImportSigners", icon: "upload_file" },
    { step: 1, labelKey: "bulkStepSetupDocument", icon: "description" },
    { step: 2, labelKey: "bulkStepReviewDocument", icon: "text_fields" },
    { step: 3, labelKey: "bulkStepSent", icon: "send" },
  ];

  private _authService = inject(AuthService);
  private _httpService = inject(HttpService);
  private _tostr = inject(ToastService);
  private _appService = inject(AppService);
  private _languageService = inject(LanguageService);
  private _router = inject(Router);
  private _createFlow = inject(CreateFlowStateService);
  private _userDetailsSub?: Subscription;
  private _routerSub?: Subscription;

  /** Drives the stepper visibility — only on the /create/new wizard. */
  isWizardRoute = false;

  /** Reactive shorthand to keep the template terse. */
  readonly currentWizardStep = computed(() => this._createFlow.wizardStep());

  readonly isSelfSignPath = computed(() => this._createFlow.signRequestPath() === "selfSign");

  readonly visibleWizardDots = computed(() => {
    if (this._createFlow.wizardFlow() === "bulk") {
      return this.wizardDotsBulk;
    }
    return this.isSelfSignPath() ? this.wizardDotsSelfSign : this.wizardDotsStandard;
  });

  readonly isFieldStep = computed(() => this.currentWizardStep() === 2);

  readonly languages: { code: SupportedLanguage; label: string }[] = [
    { code: "en", label: "English" },
    { code: "hi", label: "Hindi" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "nl", label: "Dutch" },
  ];

  ngOnInit(): void {
    this.currentLang = this._languageService.currentLanguage;
    /** Subscribe so header populates the moment user details arrive (no full-page blocker required). */
    this._userDetailsSub = this._authService.userDetails$.subscribe((u) => {
      this.userDetails = u;
    });
    /** Track route to toggle the centered wizard stepper without breaking other create-layout pages. */
    this._routerSub = this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), startWith(null))
      .subscribe(() => {
        const path = (this._router.url || "").split("?")[0].split("#")[0];
        this.isWizardRoute =
          path === "/create/new" ||
          path === "/create/bulk" ||
          /^\/edit\/[^/]+$/.test(path);
      });
  }

  ngOnDestroy(): void {
    this._userDetailsSub?.unsubscribe();
    this._routerSub?.unsubscribe();
  }

  /** Step is "complete" once the wizard has moved past it. */
  isWizardStepComplete(step: number): boolean {
    return this.currentWizardStep() > step;
  }

  isWizardStepActive(step: number): boolean {
    return this.currentWizardStep() === step;
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onToggleKapybara(): void {
    this.toggleKapybara.emit();
  }

  switchLanguage(lang: SupportedLanguage): void {
    this.currentLang = lang;
    this._languageService.setLanguage(lang);
  }

  get currentLangLabel(): string {
    return this.languages.find((l) => l.code === this.currentLang)?.label ?? "English";
  }

  logout(): void {
    this._appService.updatePageLoaderVisibility(true);
    this._httpService
      .post(this._httpService.createEndpoint(Constants.API_URI_LOGOUT))
      .subscribe(
        () => {
          this._appService.updatePageLoaderVisibility(false);
          this._authService.afterLogoutSuccess();
          this._tostr.showSuccess(Messages.MSG_SUCCESS_LOGOUT);
        },
        (error) => {
          this._httpService.showHttpError(error);
          this._appService.updatePageLoaderVisibility(false);
        }
      );
  }
}
